import { ICandidateInfo } from './../../interfaces/ICandidatoInfo';
import { ResponseModel } from 'src/app/shared/models/ResponseModel';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { StackDirectives } from 'src/app/shared/directives/stack-directives';
import { Avatar } from 'primeng/avatar';
import { environment } from 'src/environments/environment';
import { Select, SelectChangeEvent } from 'primeng/select';
import { IDropdown } from 'src/app/apps/nodo/modules/administracion/interfaces/IDropdown';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { MasterService } from 'src/app/shared/services/master.service';
import { forkJoin, catchError, of, throwError, finalize } from 'rxjs';
import { DatePicker } from 'primeng/datepicker';
import { InputMaskModule } from 'primeng/inputmask';
import { DirectionModalComponent } from 'src/app/shared/components/direction-modal/direction-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { RequisicionesCandidatoService } from 'src/app/apps/nodo/modules/seleccion/requisiciones/services/requisiciones-candidato.service';
import { ToastrService } from 'ngx-toastr';
import { AccordionModule } from 'primeng/accordion';
import { TextareaModule } from 'primeng/textarea';
import { MaterialModule } from 'src/app/material.module';
import { PersonDataBasicDto } from '../../interfaces/IPersonDataBasicDto';
import { PersonService } from '../../services/person.service';
import { CommonModule } from '@angular/common';
import { es } from 'date-fns/locale';
import { sanitizarDocumento } from 'src/app/shared/utils/documento.util';

@Component({
    selector: 'app-datos-basicos',
    imports: [
        ReactiveFormsModule,
        InputTextModule,
        StackDirectives,
        Avatar,
        Select,
        DatePicker,
        InputMaskModule,
        AccordionModule,
        TextareaModule,
        MaterialModule,
        CommonModule
    ],
    templateUrl: './datos-basicos.html',
    styleUrl: './datos-basicos.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DatosBasicos {
    private cdr = inject(ChangeDetectorRef);
    private loader = inject(LoaderService);
    private masterService = inject(MasterService);
    private dialog = inject(MatDialog);
    private ac = inject(ActivatedRoute);
    private requisitionCandidateService = inject(RequisicionesCandidatoService);
    private toastr = inject(ToastrService);
    private fb = inject(FormBuilder);
    private personService = inject(PersonService);
    private router = inject(Router);

    noPic = signal<string>(environment.noPic);

    idEncrypt: any;
    @Output() validChange = new EventEmitter<boolean>();

    identificationList: IDropdown[] = [];
    countryList: IDropdown[] = [];
    departmentCitiesExpList: IDropdown[] = [];
    civilStatusList: IDropdown[] = [];
    genderList: IDropdown[] = [];
    rhList: IDropdown[] = [];
    departmentCitiesNacList: IDropdown[] = [];
    nivelSocioEcoList: IDropdown[] = [];
    fechaNacMinDate: Date = new Date();
    fechaNacMaxDate: Date = new Date();

    form = this.fb.group({
        adjunto: [null as File | null],
        tipIde: ['', Validators.required],
        codEmp: [{ value: '', disabled: true }, [Validators.required, Validators.pattern(/^\d+$/), Validators.minLength(5)]],
        paiExp: ['', Validators.required],
        ciuExp: ['', Validators.required],
        fecExp: [null as Date | null, [Validators.required]],
        nom1Emp: ['', [Validators.required, Validators.maxLength(50)]],
        nom2Emp: ['', Validators.maxLength(50)],
        ape1Emp: ['', [Validators.required, Validators.maxLength(50)]],
        ape2Emp: ['', Validators.maxLength(50)],
        paiNac: ['', Validators.required],
        ciuNac: ['', Validators.required],
        fechaNac: [null as Date | null, [Validators.required, this.mayorDe16Validator]],
        estadoCivil: ['', Validators.required],
        genero: ['', Validators.required],
        rh: ['', Validators.required],
        // datos de contacto
        celular: ['', Validators.required],
        telefono: [''],
        correo: ['', [Validators.required, Validators.email]],
    });

    ngOnInit() {
        this.setFechaNacimientoDateLimits();

        this.ac.queryParams.subscribe((params) => {
            const rawId = params['q'];
            if (rawId) {
                this.idEncrypt = encodeURIComponent(rawId);
                this.loadData();
            } else {
                this.router.navigate(['/login']);
            }

            this.form.statusChanges.subscribe((status) => {
                this.validChange.emit(status === 'VALID');
            });
        });

        this.cdr.markForCheck();
        this.form.markAllAsTouched();
    }

    private setFechaNacimientoDateLimits(): void {
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);

        this.fechaNacMaxDate = new Date(hoy);
        this.fechaNacMaxDate.setFullYear(hoy.getFullYear() - 18);

        this.fechaNacMinDate = new Date(hoy);
        this.fechaNacMinDate.setFullYear(hoy.getFullYear() - 90);
    }

    loadData() {
        forkJoin({
            countries: this.masterService
                .getCountries()
                .pipe(catchError((err) => (err.status === 204 ? of({ data: [] }) : throwError(() => err)))),
            documents: this.masterService
                .getIdentificationTypes()
                .pipe(catchError((err) => (err.status === 204 ? of({ data: [] }) : throwError(() => err)))),
            civilStatus: this.masterService
                .getCivilStatus()
                .pipe(catchError((err) => (err.status === 204 ? of({ data: [] }) : throwError(() => err)))),
            bloodGroups: this.masterService
                .getBloodGroups()
                .pipe(catchError((err) => (err.status === 204 ? of({ data: [] }) : throwError(() => err)))),
            gender: this.masterService
                .getGendersInfoCandidate()
                .pipe(catchError((err) => (err.status === 204 ? of({ data: [] }) : throwError(() => err)))),
            person: this.requisitionCandidateService.getInfoCandidateById(this.idEncrypt),
            nivelEconomico: this.masterService
                .getEconomicLevels()
                .pipe(catchError((err) => (err.status === 204 ? of({ data: [] }) : throwError(() => err))))
        })
            .pipe(finalize(() => this.loader.hide()))
            .subscribe({
                next: ({
                    countries,
                    documents,
                    civilStatus,
                    bloodGroups,
                    gender,
                    person,
                    nivelEconomico
                }) => {
                    const dataPerson = person.data.candidateInfo;

                    this.countryList = (countries?.data ?? []).map((item: any) => ({
                        id: item.cod_pai,
                        label: item.nom_pai
                    }));
                    this.identificationList = documents.data;
                    this.civilStatusList = civilStatus.data;
                    this.rhList = bloodGroups.data;
                    this.genderList = gender.data;                    
                    this.nivelSocioEcoList = nivelEconomico.data;

                    if (person.data.imageSharepointUrl) {
                        this.noPic.set(person.data.imageSharepointUrl);
                    }
                    // Cargar datos del candidato
                    this.form.patchValue({
                        tipIde: dataPerson.idTipoIdentificacion.toString(),
                        codEmp: dataPerson.identificacion,
                        paiExp: dataPerson.idPaisExpedicion,
                        ciuExp:
                            dataPerson?.idCiuExpedicion && dataPerson?.idDepExpedicion
                                ? `${dataPerson.idCiuExpedicion}-${dataPerson.idDepExpedicion}`
                                : null,
                        fecExp: dataPerson.fechaExpedicion ? new Date(dataPerson.fechaExpedicion) : null,
                        nom1Emp: dataPerson.primerNombre,
                        nom2Emp: dataPerson.segundoNombre,
                        ape1Emp: dataPerson.primerApellido,
                        ape2Emp: dataPerson.segundoApellido,
                        paiNac: dataPerson.idPaisNacimiento,
                        ciuNac:
                            dataPerson?.idCiuNacimiento && dataPerson?.idDepNacimiento
                                ? `${dataPerson.idCiuNacimiento}-${dataPerson.idDepNacimiento}`
                                : null,
                        fechaNac: dataPerson.fechaNacimiento ? new Date(dataPerson.fechaNacimiento) : null,
                        estadoCivil: dataPerson.idEstadoCivil?.toString(),
                        genero: dataPerson.idGenero?.toString(),
                        rh: dataPerson.idTipoSangre?.toString(),
                        // datos de contacto
                        celular: dataPerson.celular,
                        telefono: dataPerson.telefono,
                        correo: dataPerson.email,
                        //datos de residencia
                    });

                    if (dataPerson.idPaisExpedicion) {
                        this.onCountryExpChange({ value: dataPerson.idPaisExpedicion });
                    }
                    if (dataPerson.idPaisNacimiento) {
                        this.onCountryNacChange({ value: dataPerson.idPaisNacimiento });
                    }
                    // if (dataPerson.idPaisResidencia) {
                    //     this.onCountryResChange({ value: dataPerson.idPaisResidencia });
                    // }

                    this.cdr.markForCheck();
                },
                error: (e) => {}
            });
    }

    onCountryExpChange(codPai: any) {
        this.getDepartmentsAndCities(codPai.value, (data) => (this.departmentCitiesExpList = data));
    }

    onCountryNacChange(codPai: any) {
        this.getDepartmentsAndCities(codPai.value, (data) => (this.departmentCitiesNacList = data));
    }

    getDepartmentsAndCities(cod_pai: string, assignCallback: (data: any[]) => void) {
        this.loader.show();
        this.masterService
            .getDepartmentsCities(cod_pai)
            .pipe(finalize(() => this.loader.hide()))
            .subscribe({
                next: (response) => {
                    assignCallback(response.data);
                    this.cdr.markForCheck();
                },
                error: (e) => {
                    this.toastr.error('Error al cargar');
                }
            });
    }

    getErrorMessage(controlName: string, errorMessage: string): string | null {
        const control = this.form.get(controlName);
        if (control?.touched) {
            if (control.hasError('required')) {
                return errorMessage ?? 'Este campo es requerido';
            }
            if (control.hasError('pattern')) {
                return 'Solo se permiten números';
            }
            if (control.hasError('minlength')) {
                const minLength = control.getError('minlength').requiredLength;
                return `Mínimo ${minLength} caracteres`;
            }
            if (control.hasError('email')) {
                return 'Correo electrónico inválido';
            }
            if (control.hasError('menorDe16')) {
                return 'Debe ser mayor de 16 años';
            }
            if (control.hasError('mayorDe90')) {
                return 'La edad máxima permitida es 90 años';
            }
        }
        return null;
    }

    saveBasicData() {
        this.loader.show('Guardando datos...');
        if (this.form.valid) {
            const payload = this.setBasicData();
            const file: File | null = this.form.value.adjunto ?? null;
            const form = this.setToFormData(payload, file);
            this.personService.updateBasicData(form).subscribe({
                next: () => {
                    this.loader.hide();
                    this.toastr.success('Datos guardados correctamente');
                    // this.stepper.next();
                },
                error: (err) => {
                    this.toastr.error('Error al guardar los datos básicos');
                    this.loader.hide();
                }
            });
        } else {
            this.form.markAllAsTouched(); // Para mostrar errores
        }
    }

    setBasicData(): PersonDataBasicDto {
        const dataDepCityExp = this.form.value.ciuExp!.split('-');
        const dataDepCityNac = this.form.value.ciuNac!.split('-');
        // const dataDepCityRes = this.form.value.ciuRes!.split('-');
        return {
            idEncrypted: this.idEncrypt,
            // El control viene deshabilitado y se llena con el valor que devuelve el backend, así que
            // puede arrastrar basura previa. Sin limpiarlo aquí se vuelve a persistir tal cual.
            identificacion: sanitizarDocumento(this.form.getRawValue().codEmp),
            idTipoIdentificacion: Number.parseInt(this.form.value.tipIde!),
            idPaisExpedicion: this.form.value.paiExp!,
            idDepExpedicion: dataDepCityExp[1].trim(),
            idCiuExpedicion: dataDepCityExp[0].trim(),
            fechaExpedicion: this.form.value.fecExp ? (this.form.value.fecExp as Date).toISOString() : '',
            primerNombre: this.form.value.nom1Emp!,
            segundoNombre: this.form.value.nom2Emp!,
            primerApellido: this.form.value.ape1Emp!,
            segundoApellido: this.form.value.ape2Emp!,
            idPaisNacimiento: this.form.value.paiNac!,
            idDepNacimiento: dataDepCityNac[1].trim(),
            idCiuNacimiento: dataDepCityNac[0].trim(),
            fechaNacimiento: this.form.value.fechaNac ? (this.form.value.fechaNac as Date).toISOString() : '',
            idEstadoCivil: Number.parseInt(this.form.value.estadoCivil!),
            idGenero: Number.parseInt(this.form.value.genero!),
            idTipoSangre: Number.parseInt(this.form.value.rh!),
            celular: this.form.value.celular!,
            telefono: this.form.value.telefono!,
            email: this.form.value.correo!,
            // idPaisResidencia: this.form.value.paiRes!,
            // idDepResidencia: dataDepCityRes[1].trim(),
            // idCiuResidencia: dataDepCityRes[0].trim(),
            // barrio: this.form.value.barrio!,
            // idTipoVivienda: Number.parseInt(this.form.value.tipoVivienda!),
            // direccion: this.form.value.direccion!,
            // nombresContactoEmergencia: this.form.value.nombreContacto!,
            // telefonosContactoEmergencia: this.form.value.telefonoContacto!,
            // deDondeObtieneIngresos: this.detailIngresos(),
            // detalleIngreso: this.form.value.ingObs || '',
            // idParentesco: Number.parseInt(this.form.value.parentesco!),
            // idEps: Number.parseInt(this.form.value.eps!),
            // idPension: Number.parseInt(this.form.value.pension!),
            // tallaCamisa: this.form.value.tallaCamisa!,
            // tallaPantalon: this.form.value.tallaPantalon!,
            // tallaZapato: this.form.value.tallaZapato!,
            // estatura: this.form.value.estatura!,
            // peso: this.form.value.peso!,
            // idNivelSocioEconomico: Number.parseInt(this.form.value.idNivelSocioEconomico!)
        };
    }

    // detailIngresos(): string {
    //     return this.form.value.ingTrabajo && this.form.value.ingOtros
    //         ? 'Trabajo o salario, otros'
    //         : this.form.value.ingTrabajo
    //           ? 'Trabajo'
    //           : this.form.value.ingOtros
    //             ? 'Otros'
    //             : 'Ninguno';
    // }

    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];

            // Asignar el archivo al form control
            this.form.patchValue({ adjunto: file });
            this.form.get('adjunto')?.updateValueAndValidity();

            // Crear URL temporal para mostrar la imagen
            const reader = new FileReader();
            reader.onload = () => {
                this.noPic.set(reader.result as string); // Angular detecta el cambio
            };
            reader.readAsDataURL(file);
        }
    }

    setToFormData(payload: PersonDataBasicDto, file?: File | null): FormData {
        const form = new FormData();

        // Helper para agregar si existe
        const appendIf = (key: string, value: any) => {
            if (value !== undefined && value !== null) {
                form.append(key, String(value));
            }
        };

        // Mapea exactamente los nombres del DTO del backend:
        appendIf('idEncrypted', payload.idEncrypted);
        appendIf('identificacion', payload.identificacion);
        appendIf('idTipoIdentificacion', payload.idTipoIdentificacion);
        appendIf('idPaisExpedicion', payload.idPaisExpedicion);
        appendIf('idDepExpedicion', payload.idDepExpedicion);
        appendIf('idCiuExpedicion', payload.idCiuExpedicion);
        appendIf('fechaExpedicion', payload.fechaExpedicion); // ya viene como ISO string
        appendIf('primerNombre', payload.primerNombre);
        appendIf('segundoNombre', payload.segundoNombre);
        appendIf('primerApellido', payload.primerApellido);
        appendIf('segundoApellido', payload.segundoApellido);
        appendIf('idPaisNacimiento', payload.idPaisNacimiento);
        appendIf('idDepNacimiento', payload.idDepNacimiento);
        appendIf('idCiuNacimiento', payload.idCiuNacimiento);
        appendIf('fechaNacimiento', payload.fechaNacimiento); // ya viene como ISO string
        appendIf('idEstadoCivil', payload.idEstadoCivil);
        appendIf('idGenero', payload.idGenero);
        appendIf('idTipoSangre', payload.idTipoSangre);
        appendIf('celular', payload.celular);
        appendIf('telefono', payload.telefono);
        appendIf('email', payload.email);
        // appendIf('idPaisResidencia', payload.idPaisResidencia);
        // appendIf('idDepResidencia', payload.idDepResidencia);
        // appendIf('idCiuResidencia', payload.idCiuResidencia);
        // appendIf('barrio', payload.barrio);
        // appendIf('idTipoVivienda', payload.idTipoVivienda);
        // appendIf('direccion', payload.direccion);
        // appendIf('nombresContactoEmergencia', payload.nombresContactoEmergencia);
        // appendIf('telefonosContactoEmergencia', payload.telefonosContactoEmergencia);
        // appendIf('deDondeObtieneIngresos', payload.deDondeObtieneIngresos);
        // appendIf('detalleIngreso', payload.detalleIngreso);
        // appendIf('idParentesco', payload.idParentesco);
        // appendIf('idEps', payload.idEps);
        // appendIf('idPension', payload.idPension);
        // appendIf('tallaCamisa', payload.tallaCamisa);
        // appendIf('tallaPantalon', payload.tallaPantalon);
        // appendIf('tallaZapato', payload.tallaZapato);
        // appendIf('estatura', payload.estatura);
        // appendIf('peso', payload.peso);
        // appendIf('idNivelSocioEconomico', payload.idNivelSocioEconomico);

        // Si tienes un adjunto opcional (por ejemplo foto/documento)
        if (file) {
            // El nombre 'Adjunto' debe coincidir con la propiedad IFormFile en tu DTO
            form.append('Image', file, file.name);
        }

        return form;
    }

    mayorDe16Validator(control: AbstractControl): ValidationErrors | null {
        const fechaSeleccionada = control.value;
        if (!fechaSeleccionada) return null;

        const hoy = new Date();
        const fechaMaximaPermitida = new Date(hoy.getFullYear() - 16, hoy.getMonth(), hoy.getDate());
        const fechaMinimaPermitida = new Date(hoy.getFullYear() - 90, hoy.getMonth(), hoy.getDate());

        if (fechaSeleccionada > fechaMaximaPermitida) {
            return { menorDe16: true };
        }

        if (fechaSeleccionada < fechaMinimaPermitida) {
            return { mayorDe90: true };
        }

        return null;
    }
}
